import React, { useState } from 'react'
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import { FileDownload as ExportIcon } from '@mui/icons-material'
import { ApiService } from '@api/index'
import { useAppSelector } from '@hooks/redux'

const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

const monthAgoIso = () => {
  const date = new Date()
  date.setDate(date.getDate() - 30)
  return date.toISOString()
}

const ExportData: React.FC = () => {
  const { hives } = useAppSelector((state) => state.locations)
  const { devices } = useAppSelector((state) => state.devices)
  const [format, setFormat] = useState<'csv' | 'json'>('csv')
  const [loading, setLoading] = useState<string | null>(null)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const hiveIds = hives.map((hive) => hive.id)
  const deviceIds = devices.map((device) => device.id)

  const exportMeasurements = async () => {
    setLoading('measurements')
    setMessage(null)
    try {
      const response = await ApiService.post(
        'measurements/export/',
        {
          device_ids: deviceIds,
          hive_ids: hiveIds,
          start_date: monthAgoIso(),
          end_date: new Date().toISOString(),
          format,
          include_metadata: true,
        },
        { responseType: 'blob' },
      )
      downloadBlob(response.data, `smartnyuki-measurements.${format}`)
      setMessage({ type: 'success', text: 'Measurement export downloaded.' })
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to export measurements.' })
    } finally {
      setLoading(null)
    }
  }

  const exportInspections = async () => {
    setLoading('inspections')
    setMessage(null)
    try {
      const start = new Date()
      start.setDate(start.getDate() - 30)
      const response = await ApiService.post(
        'inspections/export/',
        {
          hive_ids: hiveIds,
          start_date: start.toISOString().slice(0, 10),
          end_date: new Date().toISOString().slice(0, 10),
          format: format === 'json' ? 'json' : 'csv',
          include_notes: true,
          include_images: false,
        },
        { responseType: 'blob' },
      )
      downloadBlob(response.data, `smartnyuki-inspections.${format}`)
      setMessage({ type: 'success', text: 'Inspection export downloaded.' })
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to export inspections.' })
    } finally {
      setLoading(null)
    }
  }

  const exportAlerts = async () => {
    setLoading('alerts')
    setMessage(null)
    try {
      const response = await ApiService.get('alerts/')
      const payload = response.data?.data || response.data
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
      downloadBlob(blob, 'smartnyuki-alerts.json')
      setMessage({ type: 'success', text: 'Alerts export downloaded.' })
    } catch (error: any) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to export alerts.' })
    } finally {
      setLoading(null)
    }
  }

  return (
    <Container sx={{ py: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Export data
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Download hive measurements, inspections, and alerts for backup, reporting, or analysis.
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel>Format</InputLabel>
          <Select
            value={format}
            label="Format"
            onChange={(event) => setFormat(event.target.value as 'csv' | 'json')}
          >
            <MenuItem value="csv">CSV</MenuItem>
            <MenuItem value="json">JSON</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>Measurements</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Last 30 days of sensor readings for your monitors.
            </Typography>
            <Button
              variant="contained"
              startIcon={loading === 'measurements' ? <CircularProgress size={18} /> : <ExportIcon />}
              onClick={exportMeasurements}
              disabled={Boolean(loading)}
            >
              Export measurements
            </Button>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>Inspections</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Field inspection records, notes, and ratings.
            </Typography>
            <Button
              variant="contained"
              startIcon={loading === 'inspections' ? <CircularProgress size={18} /> : <ExportIcon />}
              onClick={exportInspections}
              disabled={Boolean(loading)}
            >
              Export inspections
            </Button>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>Alerts</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Active and historical alert events as JSON.
            </Typography>
            <Button
              variant="contained"
              startIcon={loading === 'alerts' ? <CircularProgress size={18} /> : <ExportIcon />}
              onClick={exportAlerts}
              disabled={Boolean(loading)}
            >
              Export alerts
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  )
}

export default ExportData
